/* A group's own settings — its name, its description, and deleting it.
 *
 * Small on purpose. A group IS a name, a description and the people in it;
 * everything else about it lives on the other tabs. Adding fields here that
 * nothing reads would be inventing scope the client never asked for.
 *
 * Delete is soft on the server: the wall and the memberships survive, so an
 * accident is recoverable by someone with database access. The dialog says
 * what actually happens rather than promising permanence it does not deliver.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field } from '@/components/ui/field'
import { useDeleteGroup, useUpdateGroup } from '@/api/queries'
import type { GroupDetail } from '@/types/api'

export function GroupSettingsPanel({
  churchId,
  group,
}: {
  churchId: string
  group: GroupDetail
}) {
  const navigate = useNavigate()
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const update = useUpdateGroup(churchId, group.id)
  const remove = useDeleteGroup(churchId)

  // Re-seed when the group changes underneath — switching groups without this
  // would leave the previous group's name in the box.
  useEffect(() => {
    setName(group.name)
    setDescription(group.description ?? '')
  }, [group.id, group.name, group.description])

  const dirty =
    name.trim() !== group.name ||
    description.trim() !== (group.description ?? '')

  function save() {
    if (!name.trim() || !dirty || update.isPending) return
    update.mutate({ name: name.trim(), description: description.trim() || null })
  }

  return (
    <div className="space-y-s4">
      <Card>
        <div className="border-b border-line-2 p-s4">
          <h2 className="text-md font-bold">Group details</h2>
        </div>

        <div className="space-y-s3 p-s4">
          <Field
            id="group-settings-name"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Field
            id="group-settings-description"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Sunday evening, ages 15–25"
            help="Shown under the group's name in the list."
          />

          <div className="flex items-center gap-s3">
            <Button
              variant="primary"
              onClick={save}
              disabled={!dirty || !name.trim()}
              loading={update.isPending}
            >
              Save changes
            </Button>
            {/* Only after a save that actually changed something — a tick that
                appears on load would be meaningless. */}
            {update.isSuccess && !dirty ? (
              <span className="text-base font-semibold text-success">Saved</span>
            ) : null}
            {update.isError ? (
              <span className="text-base text-danger">
                That did not save. Try again.
              </span>
            ) : null}
          </div>
        </div>
      </Card>

      <Card className="border-danger/30">
        <div className="border-b border-line-2 p-s4">
          <h2 className="text-md font-bold text-danger">Delete this group</h2>
        </div>
        <div className="p-s4">
          <p className="text-base text-ink-2">
            The group disappears from the list and its members lose access to
            its wall, events and polls.
          </p>
          <Button
            variant="danger"
            className="mt-s3"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete group
          </Button>
        </div>
      </Card>

      <Confirm
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete "${group.name}"?`}
        body={
          group.member_count
            ? `${group.member_count} ${group.member_count === 1 ? 'person' : 'people'} will lose access to this group, its wall, its events and its polls.`
            : 'It disappears from the Groups list.'
        }
        confirmLabel="Delete group"
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(group.id, { onSuccess: () => navigate('/groups') })
        }
      />
    </div>
  )
}
